/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { When } from "react-if";
import {
  useTable,
  useExpanded,
  useRowSelect,
  useResizeColumns,
  useFlexLayout,
  Cell,
  ColumnInstance,
  TableCellProps,
  TableHeaderProps,
} from "react-table";
import { Table, Text } from "@nordcloud/gnui";
import { isEmpty } from "~/tools";
import { StyledTable, StyledRow, StyledTh, StyledTd } from "./styles";
import { ITableProps } from "./types";

// every react-table method generates 'key' prop
/* eslint-disable react/jsx-key, @typescript-eslint/no-explicit-any */

/** This component is meant to be the next-generation `ReactTable` component built according to newest documentation, features and best practices */
export function ReactTableResizeable({
  columns,
  data,
  striped,
  hover,
  small,
  getRowProps = () => ({}),
  mb,
  initialState = {},
}: ITableProps) {
  const defaultColumn = React.useMemo(
    () => ({
      // When using the useFlexLayout:
      minWidth: 20, // minWidth is only used as a limit for resizing
      width: 150, // width is used for both the flex-basis and flex-grow
      maxWidth: 200, // maxWidth is only used as a limit for resizing
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
  } = useTable<{
    hasRightBorder: boolean;
    hasLeftBorder: boolean;
    align: Align;
  }>(
    {
      columns,
      data,
      defaultColumn,
      initialState,
    },
    useResizeColumns,
    useFlexLayout,
    useExpanded,
    useRowSelect
  );

  const showFooter = columns?.some((item: any) =>
    Object.hasOwn(item, "Footer")
  );

  if (isEmpty(rows ?? [])) {
    return <Text>No data available.</Text>;
  }

  return (
    <StyledTable
      {...getTableProps()}
      striped={striped === true}
      hoverline={hover}
      small={small}
      mb={mb}
    >
      <Table.thead>
        {headerGroups.map((headerGroup) => (
          <Table.tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              return (
                <StyledTh {...column.getHeaderProps(headerProps)}>
                  <span>{column.render("Header")}</span>
                </StyledTh>
              );
            })}
          </Table.tr>
        ))}
      </Table.thead>
      <Table.tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);

          return (
            <StyledRow
              {...row.getRowProps(getRowProps(row))}
              data-testid="table-row"
            >
              {row.cells.map((cell) => {
                // @ts-expect-error wrong types in `react-table` package
                const { hasRightBorder, hasLeftBorder } = cell.column;

                return (
                  <StyledTd
                    hasLeftBorder={hasLeftBorder}
                    hasRightBorder={hasRightBorder}
                    {...cell.getCellProps(cellProps)}
                  >
                    {cell.render("Cell")}
                  </StyledTd>
                );
              })}
            </StyledRow>
          );
        })}
      </Table.tbody>
      <Table.tfoot>
        <When condition={showFooter}>
          {footerGroups.map((group) => (
            <Table.tr {...group.getFooterGroupProps()}>
              {group.headers.map((column) => (
                <Table.td {...column.getFooterProps()}>
                  {column.render("Footer")}
                </Table.td>
              ))}
            </Table.tr>
          ))}
        </When>
      </Table.tfoot>
    </StyledTable>
  );
}

type Align = "left" | "right";

function headerProps<T extends Record<string, unknown>>(
  props: Partial<TableHeaderProps>,
  { column }: { column: ColumnInstance<T> & { align?: Align } }
) {
  return getStyles(props, column.align);
}

function cellProps<T extends Record<string, unknown>>(
  props: Partial<TableCellProps>,
  { cell }: { cell: Cell<T> & { column: { align?: Align } } }
) {
  return getStyles(props, cell.column.align);
}

function getStyles<T extends Record<string, unknown>>(
  props: T,
  align: Align = "left"
) {
  return [
    props,
    {
      style: {
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        alignItems: "flex-start",
        display: "flex",
      },
    },
  ];
}
